import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, User, ShoppingBag } from 'lucide-react';
import { LogoCircle } from './Logo';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import CurrencyDropdown from './CurrencyDropdown';

const FlagIT = ({ active, onClick, testId = 'lang-it' }) => (
  <button
    onClick={onClick}
    aria-label="Italiano"
    data-testid={testId}
    className={`transition-all duration-500 ${active ? 'opacity-100 scale-110' : 'opacity-50 hover:opacity-100'}`}
  >
    <svg width="22" height="16" viewBox="0 0 22 16">
      <rect width="7.33" height="16" fill="#009246" />
      <rect x="7.33" width="7.33" height="16" fill="#FFFFFF" />
      <rect x="14.66" width="7.33" height="16" fill="#CE2B37" />
    </svg>
  </button>
);
const FlagEN = ({ active, onClick, testId = 'lang-en' }) => (
  <button
    onClick={onClick}
    aria-label="English"
    data-testid={testId}
    className={`transition-all duration-500 ${active ? 'opacity-100 scale-110' : 'opacity-50 hover:opacity-100'}`}
  >
    <svg width="22" height="16" viewBox="0 0 60 30">
      <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFF" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#FFF" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  </button>
);

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { count: cartCount, open: openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const setLang = (lng) => { i18n.changeLanguage(lng); localStorage.setItem('harizi_lang', lng); };

  const isHome = location.pathname === '/';
  const textColor = '#171517';
  const bg = isHome
    ? `bg-white border-b border-[#E5E5E5] ${scrolled ? 'shadow-[0_8px_28px_rgba(23,21,23,0.06)]' : ''}`
    : 'glass-luxury border-b border-[#E5E5E5]';

  const navItems = [
    { to: '/anelli-fidanzamento', label: t('nav.engagement') },
    { to: '/fedi-nuziali', label: t('nav.wedding') },
    { to: '/anelli-wave', label: t('nav.wave') },
    { to: '/atelier', label: t('nav.atelier') },
    { to: '/pronta-consegna', label: t('nav.pronta') },
    { to: '/maison', label: t('nav.storia') },
    { to: '/concierge', label: t('nav.contatti') },
  ];

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${bg}`}
      >
        <div className="relative min-h-[100px] md:min-h-0 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-0 md:py-4 flex items-center justify-between gap-5">
          {/* Left: logo + hamburger mobile + nav desktop */}
          <div className="flex min-w-0 flex-1 items-center gap-6 xl:gap-9">
            <Link
              to="/"
              className="absolute left-1/2 top-1/2 z-10 flex-shrink-0 -translate-x-1/2 -translate-y-1/2 origin-center scale-[1.3] md:relative md:left-auto md:top-auto md:translate-x-0 md:translate-y-0 md:origin-left md:scale-125"
              data-testid="logo-link"
            >
              <LogoCircle size={typeof window !== 'undefined' && window.innerWidth < 768 ? 44 : 64} />
            </Link>
            <button
              data-testid="menu-toggle"
              className="relative z-20 lg:hidden"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
              style={{ color: textColor }}
            >
              {menuOpen
                ? <X className="h-7 w-7 md:h-[22px] md:w-[22px]" />
                : <Menu className="h-7 w-7 md:h-[22px] md:w-[22px]" />}
            </button>
            <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.replace('/', '')}`}
                  className="nav-link kerning-wide text-[10px] xl:text-[11px] uppercase font-medium whitespace-nowrap"
                  style={{ color: textColor }}
                >
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right: account + cart + langs */}
          <div className="relative z-20 flex flex-shrink-0 items-center gap-4 md:gap-5 justify-end">
            <div className="hidden md:flex items-center gap-3">
              <FlagIT active={i18n.language === 'it'} onClick={() => setLang('it')} />
              <FlagEN active={i18n.language === 'en'} onClick={() => setLang('en')} />
            </div>
            <div className="hidden md:block">
              <CurrencyDropdown textColor={textColor} />
            </div>
            <button
              type="button"
              onClick={openCart}
              data-testid="navbar-cart-button"
              aria-label="Cart"
              className="relative nav-link flex items-center kerning-wide text-[11px] uppercase font-medium"
              style={{ color: textColor }}
            >
              <ShoppingBag className="h-6 w-6 md:h-[18px] md:w-[18px]" />
              {cartCount > 0 && (
                <span
                  data-testid="navbar-cart-badge"
                  className="absolute -top-2 -right-2 bg-[#C9A961] text-white text-[10px] md:text-[9px] rounded-full min-w-[18px] md:min-w-[16px] h-[18px] md:h-[16px] px-1 flex items-center justify-center font-medium"
                >
                  {cartCount}
                </span>
              )}
            </button>
            {user ? (
              <div className="relative group">
                <button
                  data-testid="account-button"
                  className="nav-link flex items-center gap-2 kerning-wide text-[11px] uppercase font-medium"
                  style={{ color: textColor }}
                  onClick={() => navigate(user.role === 'admin' ? '/admin' : '/account')}
                >
                  <User className="h-[22px] w-[22px] md:h-4 md:w-4" /> <span className="hidden md:inline">{user.first_name}</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="login-link"
                className="nav-link kerning-wide text-[11px] uppercase font-medium"
                style={{ color: textColor }}
              >
                <User className="h-[22px] w-[22px] md:hidden" />
                <span className="hidden md:inline">{t('nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-40 bg-white pt-[100px] md:pt-20 px-8 lg:hidden fade-in overflow-y-auto"
        >
          <nav className="flex flex-col gap-8 mt-8 md:mt-12">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="font-serif text-[2rem] md:text-3xl text-[#0F3530] hover:text-[#C9A961] transition-colors duration-500"
                data-testid={`mobile-nav-${n.to.replace('/', '')}`}
              >
                {n.label}
              </Link>
            ))}
            <div className="diagonal-divider my-4" />
            {user ? (
              <>
                <Link to={user.role === 'admin' ? '/admin' : '/account'} className="overline text-[#0F3530]" data-testid="mobile-account-link">{user.role === 'admin' ? t('nav.admin') : t('nav.account')}</Link>
                <button onClick={logout} className="text-left overline text-[#0F3530]" data-testid="mobile-logout-button">{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="overline text-[#0F3530]" data-testid="mobile-login-link">{t('nav.login')}</Link>
                <Link to="/register" className="overline text-[#0F3530]" data-testid="mobile-register-link">{t('nav.register')}</Link>
              </>
            )}
            <div className="flex items-center gap-3 mt-6">
              <FlagIT active={i18n.language === 'it'} onClick={() => setLang('it')} testId="mobile-lang-it" />
              <FlagEN active={i18n.language === 'en'} onClick={() => setLang('en')} testId="mobile-lang-en" />
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
